import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode, Ref } from 'react';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
  /**
   * Control pegado al borde derecho del campo, dentro de su recuadro.
   *
   * Lo usa el ojo de «ver la contrasena» del acceso. Va aqui y no envuelto por
   * fuera porque tiene que quedar sobre el propio recuadro, y solo este
   * componente sabe donde empieza el input: encima esta la etiqueta y debajo el
   * error, asi que colocarlo desde la pantalla obligaria a adivinar esas dos
   * alturas. Quien lo pasa se encarga de que mida 44 px, el objetivo tactil
   * minimo de `docs/UI_STANDARDS.md`.
   */
  trailing?: ReactNode;
  ref?: Ref<HTMLInputElement>;
}

export function TextField({
  label,
  error,
  hint,
  trailing,
  id,
  className = '',
  ref,
  ...rest
}: TextFieldProps): ReactNode {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  return (
    <div>
      <label htmlFor={inputId} className="field-label">
        {label}
      </label>
      <div className="relative">
        <input
          id={inputId}
          ref={ref}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          className={[
            'block h-11 w-full rounded-card border bg-surface px-3 text-sm text-content',
            'placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/30',
            error ? 'border-danger' : 'border-line',
            trailing ? 'pr-11' : '',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          {...rest}
        />
        {trailing ? <div className="absolute right-0 top-0">{trailing}</div> : null}
      </div>
      {error ? (
        <p id={errorId} className="field-error">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="field-hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
